import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { Icon, IconSize } from "@gaming/xds-react-js";
import { Accept, Blocked, Warning } from "@gaming/xds-react-js-icons";

import {
  ClarityEvents,
  ClarityUpgradeReasons,
  trackClarityEvent,
  upgradeClaritySession,
} from "../../../clarity";
import { AddressSuggestionModal } from "../../../components/AddressSuggestionModal";
import { COUNTRIES_WITH_STATE_DROPDOWN } from "../../../components/AddressValidator/AddressValidator";
import { FormattedText } from "../../../components/FormattedText";
import { LoadingSpinner } from "../../../components/Spinner";
import { siteLocationPrefix, xboxOnboardSiteLocationPrefix } from "../../../constants";
import { blockedCountryCodes } from "../../../constants/blockedCountries";
import { isValidDunsNumber, normalizeDunsNumber } from "../../../constants/dunsNumber";
import { embargoedCountryCodes } from "../../../constants/embargoedCountries";
import { isValidLegalBusinessName } from "../../../constants/legalBusinessNames";
import { useLocalizedCountriesOrRegionsList } from "../../../helpers/countriesOrRegionsList";
import { useStateOrRegionList } from "../../../helpers/optionSets";
import { useStrings } from "../../../localization/localizationHelpers";
import { partnerCenterActions } from "../../../redux/actions/partnerCenterActions";
import { xboxOnboardActions } from "../../../redux/actions/xboxOnboardActions";
import { OnboardingRootState } from "../../../redux/reducers/rootReducer";
import {
  getAddressValidationOriginalAddress,
  getAddressValidationStatus,
  getCountryValidationsByCode,
  getSuggestedAddresses,
} from "../../../redux/selectors/partnerCenterSelectors";
import {
  XboxOnboardResponse,
  XboxOnboardSubmitRequest,
} from "../../../server/crm/api/models/xboxOnboardModels";
import { countryCrmCodeToIso } from "../../../server/crm/api/utils/countryCrmCodeToIso";
import { AddressValidationAddress } from "../../../server/partnerCenter/models/addressValidation";
import { StudioNameValidationResponse } from "../../../server/partnerCenter/models/studioNameValidation";
import * as styles from "../XboxOnboardPage.module.css";
import { FormField } from "./FormField";

const verifyStudioNameUrl = `${siteLocationPrefix}/partnercenter/verifyPartnerCenterStudioName?studioName={0}`;

// Blocked characters that ProfileVet rejects
const blockedCharsRegex = /[<>%$\[\]{}?]/; // eslint-disable-line no-useless-escape

/** Format phone number for submission — same logic as formatPhoneNumber.ts */
const formatPhoneForSubmit = (stored: string): string => {
  if (!stored) {
    return "";
  }
  const sanitized = stored.replace(/[^\d+\s]/g, "").trim();
  if (!sanitized || !/\d/.test(sanitized)) {
    return "";
  }
  // For +1 countries, keep spaces (3 parts for Oneness: CountryCode AreaCode PhoneNumber)
  if (sanitized.startsWith("+1")) {
    return sanitized;
  }
  // For all other countries, remove spaces and '+' so Oneness treats it as a single PhoneNumber part
  return sanitized.replace(/[\s+]/g, "");
};

type XboxOnboardFormProps = Readonly<{
  data: XboxOnboardResponse;
  isSubmitting: boolean;
  formId?: string;
  showActions?: boolean;
  lockBusinessIdentityFields?: boolean;
  canEditLegalEntityName?: boolean;
  canEditOwnContactDetails?: boolean;
  canEditStudioDetails?: boolean;
  partnerLogo?: React.ReactNode;
  onSubmitRequest?: (request: XboxOnboardSubmitRequest) => void;
  onValidityChange?: (isValid: boolean) => void;
  onBusinessNameChange?: (businessName: string) => void;
  businessNameTooltip?: string;
  legalEntityNameStatusWarning?: React.ReactNode;
  showLegalEntityNameDocumentationWarning?: boolean;
  businessIdentityFooter?: React.ReactNode;
  /**
   * Whether this instance is the signup form. The same component also backs the
   * settings page, where edits are not part of the onboarding funnel and would
   * otherwise corrupt the Clarity numbers.
   */
  isOnboardingFunnel?: boolean;
}>;

export const shouldLockPartnerName = (data: Pick<XboxOnboardResponse, "sellerID">): boolean =>
  Boolean(data.sellerID);

export const XboxOnboardForm = ({
  data,
  isSubmitting,
  formId,
  showActions = true,
  lockBusinessIdentityFields = false,
  canEditLegalEntityName = false,
  canEditOwnContactDetails = true,
  canEditStudioDetails = true,
  partnerLogo,
  onSubmitRequest,
  onValidityChange,
  onBusinessNameChange,
  businessNameTooltip,
  legalEntityNameStatusWarning,
  showLegalEntityNameDocumentationWarning = canEditLegalEntityName,
  businessIdentityFooter,
  isOnboardingFunnel = true,
}: XboxOnboardFormProps) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [
    firstNameLabel,
    lastNameLabel,
    businessTitleLabel,
    emailLabel,
    addressLine1Label,
    addressLine2Label,
    cityLabel,
    stateRegionLabel,
    postalCodeLabel,
    countryOrRegionLabel,
    phoneLabel,
    businessNameLabel,
    submitButtonLabel,
    partnerNameLabel,
    studioIdentityHint,
    developerToolsHint,
    partnershipTitle,
    partnershipSubtitle,
    businessTitleSubtitle,
    businessLocationHint,
    primaryBusinessAddressSubtitle,
    additionalAddressSubtitle,
    businessContactNumberHint,
    dunsNumberLabel,
    dunsNumberPlaceholder,
    dunsNumberHint,
    submitHint,
    cancelButtonLabel,
    countryNotSupportedMessage,
    sellerIdNotice,
    soleProprietorLabel,
    businessTypeLabel,
    registeredBusinessLabel,
    registeredBusinessDescription,
    registeredBusinessLegalNameInstruction,
    registeredBusinessSuffixInstruction,
    soleProprietorDescription,
    legalNameInstruction,
    tradingNameInstruction,
    documentationWarningTitle,
    documentationWarningDescription,
    documentationWarningImpact,
  ] = useStrings(
    "xboxOnboardFirstName",
    "xboxOnboardLastName",
    "xboxOnboardBusinessTitle",
    "xboxOnboardEmail",
    "xboxOnboardAddressLine1",
    "xboxOnboardAddressLine2",
    "xboxOnboardCity",
    "xboxOnboardStateRegion",
    "xboxOnboardPostalCode",
    "xboxOnboardCountryOrRegion",
    "xboxOnboardPhone",
    "xboxOnboardBusinessName",
    "xboxOnboardSubmitButton",
    "xboxOnboardPartnerNameLabel",
    "xboxOnboardStudioIdentityHint",
    "xboxOnboardDeveloperToolsHint",
    "xboxOnboardPartnershipTitle",
    "xboxOnboardPartnershipSubtitle",
    "xboxOnboardBusinessTitleSubtitle",
    "xboxOnboardBusinessLocationHint",
    "xboxOnboardPrimaryBusinessAddressSubtitle",
    "xboxOnboardAdditionalAddressSubtitle",
    "xboxOnboardBusinessContactNumberHint",
    "dunsNumberSectionTitle",
    "xboxOnboardDunsNumberPlaceholder",
    "dunsNumberSectionSubtitle",
    "xboxOnboardSubmitHint",
    "cancelButton",
    "xboxOnboardCountryNotSupported",
    "xboxOnboardSellerIdNotice",
    "partnerSettingsSoleProprietorTitle",
    "partnerSettingsBusinessTypeLegend",
    "partnerSettingsRegisteredBusinessTitle",
    "partnerSettingsRegisteredBusinessDescription",
    "partnerSettingsRegisteredBusinessLegalNameInstruction",
    "partnerSettingsRegisteredBusinessSuffixInstruction",
    "partnerSettingsSoleProprietorDescription",
    "partnerSettingsLegalNameInstruction",
    "partnerSettingsTradingNameInstruction",
    "partnerSettingsLegalNameDocumentationWarningTitle",
    "partnerSettingsLegalNameDocumentationWarningDescription",
    "partnerSettingsLegalNameDocumentationWarningImpact"
  );

  const [studioNameErrorText, studioNameSuccessText, studioNameLoadingText] = useStrings(
    "studioNameValidatorErrorText",
    "studioNameValidatorSuccessText",
    "studioNameValidatorLoadingText"
  );
  const [addressValidationLoadingText, countryValidationLoadingStatus] = useStrings(
    "addressValidatorLoadingText",
    "countryValidationLoadingStatus"
  );

  const [
    blockedCharsError,
    phoneMinDigitsError,
    invalidUSPhone,
    invalidCAPhone,
    invalidPRPhone,
    invalidPhoneFormat,
    invalidDunsNumber,
  ] = useStrings(
    "blockedCharactersError",
    "phoneMinDigitsError",
    "invalidUSPhoneNumber",
    "invalidCAPhoneNumber",
    "invalidPRPhoneNumber",
    "invalidPhoneNumberFormat",
    "invalidDunsNumber"
  );

  const [legalBusinessNameError] = useStrings("legallyRegisteredBusinessTextFieldRegexError");
  const countriesOrRegionsList = useLocalizedCountriesOrRegionsList(data.countriesOrRegions);

  const [countryOrRegionSelected, setCountryOrRegionSelected] = useState<string | undefined>(
    undefined
  );
  const [showCountrySpinner, setShowCountrySpinner] = useState(false);

  // Get country validation data (states list) from Redux
  const countryValidation = useSelector((state: OnboardingRootState) =>
    getCountryValidationsByCode(state, countryOrRegionSelected ?? "")
  );
  const formattedStates = useStateOrRegionList(countryValidation?.states);

  // Address validation state
  const addressValidationStatus = useSelector((state: OnboardingRootState) =>
    getAddressValidationStatus(state)
  );
  const suggestedAddresses = useSelector((state: OnboardingRootState) =>
    getSuggestedAddresses(state)
  );
  const originalAddress = useSelector((state: OnboardingRootState) =>
    getAddressValidationOriginalAddress(state)
  );
  const [addressValidated, setAddressValidated] = useState(false);
  const pendingSubmitRef = useRef(false);
  const prevValidationStatusRef = useRef(addressValidationStatus);

  // Local form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [businessTitle, setBusinessTitle] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [stateRegion, setStateRegion] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [countryOrRegion, setCountryOrRegion] = useState("");
  const [phoneDisplay, setPhoneDisplay] = useState("");
  const [phoneStored, setPhoneStored] = useState("");
  const fieldIdPrefix = useId();
  const partnerNameId = `${fieldIdPrefix}-partner-name`;
  const partnerNameHintId = `${partnerNameId}-hint`;
  const partnerNameStatusId = `${partnerNameId}-status`;
  const emailId = `${fieldIdPrefix}-email`;
  const countryId = `${fieldIdPrefix}-country`;
  const countryMessageId = `${countryId}-message`;
  const stateRegionId = `${fieldIdPrefix}-state-region`;
  const phoneId = `${fieldIdPrefix}-phone`;
  const phoneErrorId = `${phoneId}-error`;
  const phoneHintId = `${phoneId}-hint`;
  const dunsNumberId = `${fieldIdPrefix}-duns-number`;
  const dunsNumberErrorId = `${dunsNumberId}-error`;
  const dunsNumberHintId = `${dunsNumberId}-hint`;
  const accordionHeaderId = `${fieldIdPrefix}-partnership-header`;
  const accordionPanelId = `${fieldIdPrefix}-partnership-panel`;
  const registeredBusinessId = `${fieldIdPrefix}-registered-business`;
  const soleProprietorId = `${fieldIdPrefix}-sole-proprietor`;
  const [dunsNumber, setDunsNumber] = useState("");
  const [studioName, setStudioName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [isSoleProprietor, setIsSoleProprietor] = useState(false);
  const studioFieldsDisabled = isSubmitting || !canEditStudioDetails;
  const businessIdentityDisabled =
    isSubmitting ||
    (!canEditStudioDetails && !canEditLegalEntityName) ||
    (lockBusinessIdentityFields && !canEditLegalEntityName);
  const hasStoredStudioAddress = Boolean(
    addressLine1 || addressLine2 || city || stateRegion || postalCode || phoneStored
  );
  const shouldShowStudioAddress =
    Boolean(countryOrRegionSelected) || (!canEditStudioDetails && hasStoredStudioAddress);

  // Phone country code from country validation data
  const phoneCountryCode = countryValidation?.phoneCountryCode ?? "";
  const phoneNumberRegex = countryValidation?.phoneNumberRegex ?? "",
    prevPhoneCountryCodeRef = useRef(phoneCountryCode),
    // Non-+1 numbers are saved as bare "{code}{digits}" (Oneness format); strip the code once known.
    stripLoadedPhoneCodeRef = useRef(false);

  const isEmbargoedCountry = useMemo(() => {
    if (!countryOrRegion) {
      return false;
    }
    const iso = countryCrmCodeToIso[countryOrRegion] ?? countryOrRegion;
    return embargoedCountryCodes.has(iso);
  }, [countryOrRegion]);

  const isBlockedCountry = useMemo(() => {
    if (!countryOrRegion) {
      return false;
    }
    const iso = countryCrmCodeToIso[countryOrRegion] ?? countryOrRegion;
    return blockedCountryCodes.has(iso);
  }, [countryOrRegion]);

  // A partner who reaches the unsupported region wall has no path forward, so
  // the session is upgraded past Clarity's daily sampling to guarantee replay.
  useEffect(() => {
    if (!isOnboardingFunnel || !isBlockedCountry) {
      return;
    }
    trackClarityEvent(ClarityEvents.onboardFormBlockedCountry);
    upgradeClaritySession(ClarityUpgradeReasons.blockedCountry);
  }, [isBlockedCountry, isOnboardingFunnel]);

  // When country changes, reformat phone for new country code
  useEffect(() => {
    if (
      (prevPhoneCountryCodeRef.current !== phoneCountryCode || stripLoadedPhoneCodeRef.current) &&
      phoneCountryCode &&
      phoneDisplay
    ) {
      let digits = phoneDisplay.replace(/\D/g, "");
      if (stripLoadedPhoneCodeRef.current && digits.startsWith(phoneCountryCode)) {
        digits = digits.slice(phoneCountryCode.length);
        setPhoneDisplay(digits);
      }
      stripLoadedPhoneCodeRef.current = false;
      let newStored: string;
      if (phoneCountryCode === "1" && digits.length >= 3) {
        newStored = `+${phoneCountryCode} ${digits.slice(0, 3)} ${digits.slice(3)}`;
      } else {
        newStored = digits.length > 0 ? `+${phoneCountryCode} ${digits}` : "";
      }
      setPhoneStored(newStored);
    }
    prevPhoneCountryCodeRef.current = phoneCountryCode;
  }, [phoneCountryCode, phoneDisplay]);

  const handlePhoneChange = (rawInput: string) => {
    const digits = rawInput.replace(/[^0-9]/g, "");
    stripLoadedPhoneCodeRef.current = false;
    setPhoneDisplay(digits);

    if (!phoneCountryCode) {
      setPhoneStored(digits);
      return;
    }

    let stored: string;
    if (phoneCountryCode === "1" && digits.length >= 3) {
      stored = `+${phoneCountryCode} ${digits.slice(0, 3)} ${digits.slice(3)}`;
    } else {
      stored = digits.length > 0 ? `+${phoneCountryCode} ${digits}` : "";
    }
    setPhoneStored(stored);
  };

  const getBlockedCharsError = (value: string): string | undefined =>
    blockedCharsRegex.test(value) ? blockedCharsError : undefined;

  const getPhoneError = (): string | undefined => {
    if (!phoneDisplay) {
      return undefined;
    }
    const digits = phoneDisplay.replace(/\D/g, "");
    if (digits.length < 4) {
      return phoneMinDigitsError;
    }
    if (phoneNumberRegex) {
      const isoCountry = countryCrmCodeToIso[countryOrRegion] ?? countryOrRegion;
      const validationIso = isoCountry === "PR" ? "US" : isoCountry;
      try {
        const regex = new RegExp(phoneNumberRegex);
        if (!regex.test(digits)) {
          if (validationIso === "US") {
            return isoCountry === "PR" ? invalidPRPhone : invalidUSPhone;
          }
          if (validationIso === "CA") {
            return invalidCAPhone;
          }
          return invalidPhoneFormat;
        }
      } catch {
        // If regex is invalid, skip validation
      }
    }
    return undefined;
  };

  const phoneError = getPhoneError();
  const dunsNumberError =
    dunsNumber.trim() && !isValidDunsNumber(dunsNumber) ? invalidDunsNumber : undefined;
  const businessNameError =
    (!lockBusinessIdentityFields || canEditLegalEntityName) &&
    businessName.trim() &&
    !isValidLegalBusinessName(businessName)
      ? legalBusinessNameError
      : undefined;
  const canChooseBusinessType = isOnboardingFunnel || canEditLegalEntityName;
  const businessTypeOptions = [
    {
      isSoleProprietor: false,
      id: registeredBusinessId,
      value: "registered-business",
      label: registeredBusinessLabel,
      description: registeredBusinessDescription,
      instructions: [registeredBusinessLegalNameInstruction, registeredBusinessSuffixInstruction],
    },
    {
      isSoleProprietor: true,
      id: soleProprietorId,
      value: "sole-proprietor",
      label: soleProprietorLabel,
      description: soleProprietorDescription,
      instructions: [legalNameInstruction, tradingNameInstruction],
    },
  ] as const;

  // Studio name validation state
  const [isStudioNameValidating, setIsStudioNameValidating] = useState(false);
  const [studioNameValidationResult, setStudioNameValidationResult] = useState<boolean | null>(
    null
  );
  const [validatedStudioName, setValidatedStudioName] = useState<string | null>(null);

  const isPartnerNameLocked = lockBusinessIdentityFields || shouldLockPartnerName(data);
  const normalizedStudioName = studioName.trim();

  const handleStudioNameChange = (value: string) => {
    setStudioName(value);
    if (value.trim() !== validatedStudioName) {
      setIsStudioNameValidating(false);
      setStudioNameValidationResult(null);
      setValidatedStudioName(null);
    }
  };

  // Auto-validate studio name after user stops typing (debounce 800ms)
  useEffect(() => {
    if (!normalizedStudioName || isPartnerNameLocked) {
      return;
    }
    if (normalizedStudioName === validatedStudioName) {
      return;
    }

    let isCancelled = false;

    const timer = setTimeout(async () => {
      setIsStudioNameValidating(true);
      try {
        const response = await fetch(
          verifyStudioNameUrl.replace("{0}", encodeURIComponent(normalizedStudioName))
        );
        const result = (await response.json()) as StudioNameValidationResponse;
        if (!isCancelled) {
          setStudioNameValidationResult(result.isStudioNameAvailable);
          setValidatedStudioName(normalizedStudioName);
          if (isOnboardingFunnel && !result.isStudioNameAvailable) {
            trackClarityEvent(ClarityEvents.onboardStudioNameConflict);
          }
        }
      } catch {
        if (!isCancelled) {
          setStudioNameValidationResult(false);
        }
      } finally {
        if (!isCancelled) {
          setIsStudioNameValidating(false);
        }
      }
    }, 800);

    return () => {
      isCancelled = true;
      clearTimeout(timer);
    };
  }, [normalizedStudioName, validatedStudioName, isPartnerNameLocked, isOnboardingFunnel]);

  // Fetch country validation when country changes
  useEffect(() => {
    if (countryOrRegionSelected) {
      dispatch(partnerCenterActions.fetchCountryValidation.start(countryOrRegionSelected));
      setTimeout(() => {
        setShowCountrySpinner(false);
      }, 300);
    }
  }, [countryOrRegionSelected, dispatch]);

  const handleCountryChange = (value: string) => {
    setCountryOrRegion(value);
    if (!value) {
      setCountryOrRegionSelected(undefined);
      return;
    }
    if (value !== countryOrRegionSelected) {
      setShowCountrySpinner(true);
      setCountryOrRegionSelected(value);
      setAddressLine1("");
      setAddressLine2("");
      setCity("");
      setStateRegion("");
      setPostalCode("");
    }
  };

  // Populate form when data loads
  useEffect(() => {
    if (data) {
      setFirstName(data.contact.firstName ?? "");
      setLastName(data.contact.lastName ?? "");
      setBusinessTitle(data.contact.businessTitle ?? "");
      setAddressLine1(data.contact.addressLine1 ?? "");
      setAddressLine2(data.contact.addressLine2 ?? "");
      setCity(data.contact.city ?? "");
      setStateRegion(data.contact.stateRegion ?? "");
      setPostalCode(data.contact.postalCode ?? "");
      const country = data.contact.countryOrRegion ?? "";
      setCountryOrRegion(country);
      if (country) {
        setCountryOrRegionSelected(country);
      }
      const rawPhone = data.contact.phone ?? "";
      // Strip any existing +{code} prefix to get display digits
      const displayDigits = rawPhone.replace(/^\+\d+\s*/, "").replace(/\D/g, "");
      stripLoadedPhoneCodeRef.current = Boolean(displayDigits) && !rawPhone.startsWith("+");
      setPhoneDisplay(displayDigits);
      setPhoneStored(rawPhone);
      setDunsNumber(data.dunsNumber ?? "");
      setStudioName(data.studio.studioName ?? "");
      setBusinessName(data.studio.businessName ?? "");
      setIsSoleProprietor(data.studio.isSoleProprietor === true);
    }
  }, [data]);

  const hasBlockedChars =
    getBlockedCharsError(firstName) ||
    getBlockedCharsError(lastName) ||
    getBlockedCharsError(addressLine1) ||
    getBlockedCharsError(addressLine2) ||
    getBlockedCharsError(city);

  const isFormValid =
    firstName.trim() &&
    lastName.trim() &&
    (!isOnboardingFunnel || businessTitle.trim()) &&
    countryOrRegion &&
    !isBlockedCountry &&
    addressLine1.trim() &&
    city.trim() &&
    stateRegion.trim() &&
    postalCode.trim() &&
    phoneDisplay.trim() &&
    !phoneError &&
    !hasBlockedChars &&
    studioName.trim() &&
    businessName.trim() &&
    !businessNameError &&
    (isPartnerNameLocked || studioNameValidationResult === true) &&
    !dunsNumberError;

  useEffect(() => {
    onValidityChange?.(Boolean(isFormValid));
  }, [isFormValid, onValidityChange]);

  // Reset address validation when address fields change
  const addressKey = `${countryOrRegion}|${stateRegion}|${city}|${addressLine1}|${addressLine2}|${postalCode}`;
  const prevAddressKeyRef = useRef(addressKey);
  const skipResetRef = useRef(false);
  useEffect(() => {
    if (prevAddressKeyRef.current !== addressKey) {
      prevAddressKeyRef.current = addressKey;
      if (skipResetRef.current) {
        skipResetRef.current = false;
      } else {
        setAddressValidated(false);
        dispatch(partnerCenterActions.resetAddressValidation());
      }
    }
  }, [addressKey, dispatch]);

  const doSubmit = useCallback(() => {
    const request: XboxOnboardSubmitRequest = {
      contact: {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        businessTitle: businessTitle.trim(),
        email: data?.contact.email ?? "",
        addressLine1: addressLine1.trim(),
        addressLine2: addressLine2.trim(),
        city: city.trim(),
        stateRegion: stateRegion.trim(),
        postalCode: postalCode.trim(),
        countryOrRegion: countryOrRegion.trim(),
        phone: formatPhoneForSubmit(phoneStored),
        useDifferentShippingAddress: false,
        shippingAddressLine1: "",
        shippingAddressLine2: "",
        shippingCity: "",
        shippingStateRegion: "",
        shippingPostalCode: "",
        shippingCountryOrRegion: "",
        shippingPhone: "",
      },
      studio: {
        studioName: studioName.trim(),
        partnerCenterVerifiedStudioName: validatedStudioName ?? studioName.trim(),
        businessName: businessName.trim(),
        studioWebsite: data?.studio.studioWebsite ?? "",
        isSoleProprietor,
      },
      sellerID: data?.sellerID,
      dunsNumber: dunsNumber.trim() ? normalizeDunsNumber(dunsNumber) : undefined,
    };

    if (onSubmitRequest) {
      onSubmitRequest(request);
      return;
    }

    trackClarityEvent(ClarityEvents.onboardFormSubmitted);
    dispatch(xboxOnboardActions.submitForm.start(request));
  }, [
    firstName,
    lastName,
    businessTitle,
    data?.contact.email,
    addressLine1,
    addressLine2,
    city,
    stateRegion,
    postalCode,
    countryOrRegion,
    phoneStored,
    studioName,
    validatedStudioName,
    businessName,
    isSoleProprietor,
    data?.studio.studioWebsite,
    data?.sellerID,
    dunsNumber,
    onSubmitRequest,
    dispatch,
  ]);

  // Keep a stable ref to doSubmit so async effects can call the latest version
  const doSubmitRef = useRef(doSubmit);
  doSubmitRef.current = doSubmit;

  // React to address validation status changes
  useEffect(() => {
    if (prevValidationStatusRef.current === addressValidationStatus) {
      return;
    }
    prevValidationStatusRef.current = addressValidationStatus;

    if (addressValidationStatus === "valid" || addressValidationStatus === "failed") {
      // "valid" = address verified; "failed" = validation service error, proceed anyway
      setAddressValidated(true);
      if (pendingSubmitRef.current) {
        pendingSubmitRef.current = false;
        doSubmitRef.current();
      }
    }
    // "suggestions" → modal will show
  }, [addressValidationStatus]);

  const handleSubmit = useCallback(() => {
    if (!isFormValid) {
      if (isOnboardingFunnel) {
        trackClarityEvent(ClarityEvents.onboardFormValidationFailed);
      }
      return;
    }

    // Skip validation when the saved address is read-only.
    if (!canEditStudioDetails || addressValidated) {
      doSubmit();
      return;
    }

    // Trigger address validation — submission will proceed after validation resolves
    pendingSubmitRef.current = true;
    const isoCountry = countryCrmCodeToIso[countryOrRegion] ?? countryOrRegion;
    dispatch(
      partnerCenterActions.validateAddress.start({
        country: isoCountry,
        region: stateRegion,
        city,
        address_line1: addressLine1,
        address_line2: addressLine2,
        postal_code: postalCode,
      })
    );
  }, [
    isFormValid,
    isOnboardingFunnel,
    canEditStudioDetails,
    addressValidated,
    doSubmit,
    countryOrRegion,
    stateRegion,
    city,
    addressLine1,
    addressLine2,
    postalCode,
    dispatch,
  ]);

  // Handle address suggestion selection
  const [submitAfterSuggestion, setSubmitAfterSuggestion] = useState(false);
  const handleSuggestionConfirm = useCallback(
    (address: AddressValidationAddress) => {
      skipResetRef.current = true;
      if (address.city) {
        setCity(address.city);
      }
      if (address.address_line1) {
        setAddressLine1(address.address_line1);
      }
      setAddressLine2(address.address_line2 ?? "");
      if (address.postal_code) {
        setPostalCode(address.postal_code);
      }
      // Only overwrite region for countries without a state dropdown
      const isoCountry = countryCrmCodeToIso[countryOrRegion] ?? countryOrRegion;
      if (address.region && !COUNTRIES_WITH_STATE_DROPDOWN.has(isoCountry)) {
        setStateRegion(address.region);
      }
      dispatch(partnerCenterActions.confirmAddressSuggestion(address));
      setAddressValidated(true);
      if (isOnboardingFunnel) {
        trackClarityEvent(ClarityEvents.onboardAddressSuggestionAccepted);
      }
      // Schedule submit on next render so state updates are applied
      if (pendingSubmitRef.current) {
        pendingSubmitRef.current = false;
        setSubmitAfterSuggestion(true);
      }
    },
    [countryOrRegion, dispatch, isOnboardingFunnel]
  );

  // Auto-submit after suggestion confirmation (state has settled)
  useEffect(() => {
    if (submitAfterSuggestion) {
      setSubmitAfterSuggestion(false);
      doSubmitRef.current();
    }
  }, [submitAfterSuggestion]);

  const handleSuggestionReject = useCallback(() => {
    pendingSubmitRef.current = false;
    if (isOnboardingFunnel) {
      trackClarityEvent(ClarityEvents.onboardAddressSuggestionRejected);
    }
    dispatch(partnerCenterActions.resetAddressValidation());
  }, [dispatch, isOnboardingFunnel]);

  const isSuggestionModalOpen =
    canEditStudioDetails &&
    addressValidationStatus === "suggestions" &&
    suggestedAddresses.length > 0;

  useEffect(() => {
    if (isOnboardingFunnel && isSuggestionModalOpen) {
      trackClarityEvent(ClarityEvents.onboardAddressSuggestionShown);
    }
  }, [isSuggestionModalOpen, isOnboardingFunnel]);

  const [isAccordionOpen, setIsAccordionOpen] = useState(true);

  return (
    <>
      <form
        id={formId}
        aria-busy={isSubmitting || (canEditStudioDetails && addressValidationStatus === "loading")}
        onSubmit={event => {
          event.preventDefault();
          handleSubmit();
        }}
      >
        <div className={styles.sectionBox}>
          {canEditStudioDetails && addressValidationStatus === "loading" && (
            <span
              className={styles.screenReaderOnly}
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              {addressValidationLoadingText}
            </span>
          )}
          {/* Partner name at top */}
          {!isBlockedCountry && (
            <div className={partnerLogo ? styles.partnerIdentitySection : undefined}>
              {partnerLogo}
              <div className={styles.partnerNameSection}>
                <label className={styles.formLabel} htmlFor={partnerNameId}>
                  {partnerNameLabel}
                  <span className={styles.requiredMark} aria-hidden="true">
                    *
                  </span>
                </label>
                <div className={styles.partnerNameRow}>
                  <input
                    id={partnerNameId}
                    type="text"
                    className={styles.formInput}
                    value={studioName}
                    onChange={e => handleStudioNameChange(e.target.value)}
                    disabled={studioFieldsDisabled || isPartnerNameLocked}
                    required
                    aria-required="true"
                    aria-invalid={studioNameValidationResult === false}
                    aria-describedby={
                      isPartnerNameLocked && data.sellerID
                        ? `${partnerNameHintId} ${partnerNameStatusId}`
                        : !isPartnerNameLocked && studioName.trim()
                        ? `${partnerNameHintId} ${partnerNameStatusId}`
                        : partnerNameHintId
                    }
                  />
                  {!isPartnerNameLocked && studioName.trim() && (
                    <span
                      id={partnerNameStatusId}
                      className={
                        isStudioNameValidating
                          ? styles.validationPending
                          : studioNameValidationResult === true
                          ? styles.validationSuccess
                          : studioNameValidationResult === false
                          ? styles.validationError
                          : styles.validationPending
                      }
                      role="status"
                      aria-live="polite"
                      aria-atomic="true"
                    >
                      {isStudioNameValidating ? (
                        studioNameLoadingText
                      ) : studioNameValidationResult === true ? (
                        <>
                          <Icon aria-hidden="true" icon={Accept} iconSize={IconSize.Small} />
                          <span>{studioNameSuccessText}</span>
                        </>
                      ) : studioNameValidationResult === false ? (
                        studioNameErrorText
                      ) : (
                        ""
                      )}
                    </span>
                  )}
                </div>
                {isPartnerNameLocked && data.sellerID && (
                  <span id={partnerNameStatusId} className={styles.sellerIdNotice}>
                    {sellerIdNotice.replace("{0}", data.sellerID)}
                  </span>
                )}
                <p id={partnerNameHintId} className={styles.fieldHint}>
                  {studioIdentityHint}
                  <br />
                  <FormattedText text={developerToolsHint} />
                </p>
              </div>
            </div>
          )}

          {/* Accordion: About the partnership */}
          <div className={styles.accordionSection}>
            <button
              id={accordionHeaderId}
              type="button"
              className={styles.accordionHeader}
              onClick={() => setIsAccordionOpen(!isAccordionOpen)}
              aria-expanded={isAccordionOpen}
              aria-controls={accordionPanelId}
            >
              <div className={styles.accordionHeaderLeft}>
                <span className={styles.accordionIcon} aria-hidden="true">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M20 7H4C2.9 7 2 7.9 2 9V19C2 20.1 2.9 21 4 21H20C21.1 21 22 20.1 22 19V9C22 7.9 21.1 7 20 7ZM14 3H10C8.9 3 8 3.9 8 5V7H16V5C16 3.9 15.1 3 14 3Z"
                      fill="white"
                    />
                  </svg>
                </span>
                <div>
                  <span className={styles.accordionTitle}>{partnershipTitle}</span>
                  <span className={styles.accordionSubtitle}>{partnershipSubtitle}</span>
                </div>
              </div>
              <span
                aria-hidden="true"
                className={`${styles.accordionChevron} ${
                  isAccordionOpen ? styles.accordionChevronOpen : ""
                }`}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4 10L8 6L12 10"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </button>
            {isAccordionOpen && (
              <div
                id={accordionPanelId}
                className={styles.accordionContent}
                role="region"
                aria-labelledby={accordionHeaderId}
              >
                <div className={styles.formGrid}>
                  {!isBlockedCountry && (
                    <>
                      {!isOnboardingFunnel && legalEntityNameStatusWarning && (
                        <div className={styles.businessTypeStatus}>
                          {legalEntityNameStatusWarning}
                        </div>
                      )}
                      {!isOnboardingFunnel &&
                        !legalEntityNameStatusWarning &&
                        showLegalEntityNameDocumentationWarning && (
                          <div className={styles.businessTypeNotice} role="status">
                            <Icon
                              aria-hidden="true"
                              className={styles.businessTypeNoticeIcon}
                              icon={Warning}
                              iconSize={IconSize.Custom}
                            />
                            <div className={styles.businessTypeNoticeText}>
                              <strong>{documentationWarningTitle}</strong>
                              <span>{documentationWarningDescription}</span>
                              <span>{documentationWarningImpact}</span>
                            </div>
                          </div>
                        )}
                      <fieldset
                        className={styles.businessTypeGroup}
                        data-onboarding-funnel={isOnboardingFunnel}
                        disabled={businessIdentityDisabled}
                      >
                        <legend className={styles.businessTypeLegend}>{businessTypeLabel}</legend>
                        <div className={styles.businessTypeOptions}>
                          {businessTypeOptions.map(option => {
                            const isSelected = option.isSoleProprietor === isSoleProprietor;
                            if (!isSelected && !canChooseBusinessType) {
                              return null;
                            }

                            return (
                              <div
                                key={option.value}
                                className={`${styles.businessTypeCard} ${
                                  isSelected ? styles.businessTypeCardSelected : ""
                                }`}
                              >
                                <label
                                  className={styles.businessTypeHeader}
                                  htmlFor={option.id}
                                  aria-label={option.label}
                                >
                                  <span className={styles.businessTypeText}>
                                    <span className={styles.businessTypeTitle}>{option.label}</span>
                                    <span className={styles.businessTypeDescription}>
                                      {option.description}
                                    </span>
                                  </span>
                                  {canChooseBusinessType && (
                                    <input
                                      id={option.id}
                                      type="radio"
                                      name={`${fieldIdPrefix}-business-type`}
                                      value={option.value}
                                      checked={isSelected}
                                      onChange={() => setIsSoleProprietor(option.isSoleProprietor)}
                                    />
                                  )}
                                </label>
                                {isSelected && (
                                  <div className={styles.businessTypeField}>
                                    <FormField
                                      label={businessNameLabel}
                                      value={businessName}
                                      onChange={value => {
                                        setBusinessName(value);
                                        onBusinessNameChange?.(value);
                                      }}
                                      required
                                      disabled={businessIdentityDisabled}
                                      error={businessNameError}
                                      tooltip={businessNameTooltip}
                                    />
                                    <ul className={styles.businessTypeInstructions}>
                                      {option.instructions.map(instruction => (
                                        <li key={`${option.value}-${instruction}`}>
                                          {instruction}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </fieldset>
                      {businessIdentityFooter}
                      <FormField
                        label={firstNameLabel}
                        value={firstName}
                        onChange={setFirstName}
                        required
                        disabled={isSubmitting || !canEditOwnContactDetails}
                        maxLength={50}
                        error={getBlockedCharsError(firstName)}
                      />
                      <FormField
                        label={lastNameLabel}
                        value={lastName}
                        onChange={setLastName}
                        required
                        disabled={isSubmitting || !canEditOwnContactDetails}
                        maxLength={50}
                        error={getBlockedCharsError(lastName)}
                      />
                      <FormField
                        label={businessTitleLabel}
                        value={businessTitle}
                        onChange={setBusinessTitle}
                        required={isOnboardingFunnel}
                        disabled={isSubmitting || !canEditOwnContactDetails}
                        maxLength={50}
                        subtitle={businessTitleSubtitle}
                      />
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel} htmlFor={emailId}>
                          {emailLabel}
                        </label>
                        <input
                          id={emailId}
                          className={styles.formInput}
                          type="email"
                          value={data?.contact.email ?? ""}
                          disabled
                          readOnly
                        />
                      </div>
                    </>
                  )}
                  <hr />
                  <div className={styles.formGroupFull}>
                    <label className={styles.formLabel} htmlFor={countryId}>
                      {countryOrRegionLabel}
                      <span className={styles.requiredMark} aria-hidden="true">
                        *
                      </span>
                    </label>
                    <select
                      id={countryId}
                      className={styles.formInput}
                      value={countryOrRegion}
                      onChange={e => handleCountryChange(e.target.value)}
                      disabled={studioFieldsDisabled || lockBusinessIdentityFields}
                      required
                      aria-required="true"
                      aria-invalid={isBlockedCountry}
                      aria-describedby={countryMessageId}
                    >
                      {countriesOrRegionsList.map(option => (
                        <option key={String(option.value)} value={String(option.value ?? "")}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {isBlockedCountry ? (
                      <p
                        id={countryMessageId}
                        className={styles.blockedRegionMessage}
                        role="alert"
                        aria-live="assertive"
                      >
                        <Icon aria-hidden="true" icon={Blocked} iconSize={IconSize.Custom} />
                        <span>{countryNotSupportedMessage}</span>
                      </p>
                    ) : (
                      <p id={countryMessageId} className={styles.fieldHint}>
                        {businessLocationHint}
                      </p>
                    )}
                  </div>
                  {showCountrySpinner && !isBlockedCountry && (
                    <div
                      className={styles.spinnerContainer}
                      role="status"
                      aria-live="polite"
                      aria-label={countryValidationLoadingStatus}
                    >
                      <LoadingSpinner />
                    </div>
                  )}
                  {!showCountrySpinner && shouldShowStudioAddress && !isBlockedCountry && (
                    <>
                      <FormField
                        label={addressLine1Label}
                        value={addressLine1}
                        onChange={setAddressLine1}
                        required
                        disabled={studioFieldsDisabled}
                        maxLength={50}
                        error={getBlockedCharsError(addressLine1)}
                        subtitle={primaryBusinessAddressSubtitle}
                      />
                      <FormField
                        label={addressLine2Label}
                        value={addressLine2}
                        onChange={setAddressLine2}
                        disabled={studioFieldsDisabled}
                        maxLength={50}
                        error={getBlockedCharsError(addressLine2)}
                        subtitle={additionalAddressSubtitle}
                      />
                      <FormField
                        label={cityLabel}
                        value={city}
                        onChange={setCity}
                        required
                        disabled={studioFieldsDisabled}
                        maxLength={64}
                        error={getBlockedCharsError(city)}
                      />
                      {formattedStates ? (
                        <div className={styles.formGroup}>
                          <label className={styles.formLabel} htmlFor={stateRegionId}>
                            {stateRegionLabel}
                            <span className={styles.requiredMark} aria-hidden="true">
                              *
                            </span>
                          </label>
                          <select
                            id={stateRegionId}
                            className={styles.formInput}
                            value={stateRegion}
                            onChange={e => setStateRegion(e.target.value)}
                            disabled={studioFieldsDisabled}
                            required
                            aria-required="true"
                          >
                            {formattedStates.map(option => (
                              <option key={String(option.value)} value={String(option.value ?? "")}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        <FormField
                          label={stateRegionLabel}
                          value={stateRegion}
                          onChange={setStateRegion}
                          required
                          disabled={studioFieldsDisabled}
                        />
                      )}
                      <FormField
                        label={postalCodeLabel}
                        value={postalCode}
                        onChange={setPostalCode}
                        required
                        disabled={studioFieldsDisabled}
                        maxLength={15}
                      />
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel} htmlFor={phoneId}>
                          {phoneLabel}
                          <span className={styles.requiredMark} aria-hidden="true">
                            *
                          </span>
                        </label>
                        <div className={styles.phoneInputRow}>
                          {phoneCountryCode && (
                            <span className={styles.countryCodePrefix}>+{phoneCountryCode}</span>
                          )}
                          <input
                            id={phoneId}
                            type="text"
                            className={styles.formInput}
                            value={phoneDisplay}
                            onChange={e => handlePhoneChange(e.target.value)}
                            disabled={studioFieldsDisabled}
                            maxLength={15}
                            required
                            aria-required="true"
                            aria-invalid={Boolean(phoneError)}
                            aria-describedby={`${
                              phoneError ? phoneErrorId : ""
                            } ${phoneHintId}`.trim()}
                          />
                        </div>
                        {phoneError && (
                          <span id={phoneErrorId} className={styles.validationError} role="alert">
                            {phoneError}
                          </span>
                        )}
                        <p id={phoneHintId} className={styles.fieldHint}>
                          {businessContactNumberHint}
                        </p>
                      </div>
                      {isEmbargoedCountry && (
                        <div className={styles.formGroup}>
                          <label className={styles.formLabel} htmlFor={dunsNumberId}>
                            {dunsNumberLabel}
                          </label>
                          <input
                            id={dunsNumberId}
                            type="text"
                            className={styles.formInput}
                            value={dunsNumber}
                            onChange={e => setDunsNumber(e.target.value)}
                            placeholder={dunsNumberPlaceholder}
                            disabled={studioFieldsDisabled}
                            inputMode="numeric"
                            maxLength={11}
                            aria-invalid={Boolean(dunsNumberError)}
                            aria-describedby={`${
                              dunsNumberError ? dunsNumberErrorId : ""
                            } ${dunsNumberHintId}`.trim()}
                          />
                          {dunsNumberError && (
                            <span
                              id={dunsNumberErrorId}
                              className={styles.validationError}
                              role="alert"
                            >
                              {dunsNumberError}
                            </span>
                          )}
                          <p id={dunsNumberHintId} className={styles.fieldHint}>
                            {dunsNumberHint}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {showActions && (
            <div className={styles.submitRow}>
              {!isBlockedCountry && (
                <span className={styles.submitHint}>
                  <FormattedText text={submitHint} />
                </span>
              )}
              <div className={styles.submitActions}>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={() => history.push(`${xboxOnboardSiteLocationPrefix}/signup`)}
                >
                  {cancelButtonLabel}
                </button>
                {!isBlockedCountry && (
                  <button
                    type="submit"
                    className={styles.submitButton}
                    disabled={!isFormValid || isSubmitting}
                  >
                    {submitButtonLabel}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </form>

      <AddressSuggestionModal
        isOpen={isSuggestionModalOpen}
        suggestedAddresses={suggestedAddresses}
        originalAddress={
          originalAddress ?? {
            country: countryCrmCodeToIso[countryOrRegion] ?? countryOrRegion,
            region: stateRegion,
            city,
            address_line1: addressLine1,
            address_line2: addressLine2,
            postal_code: postalCode,
          }
        }
        onConfirm={handleSuggestionConfirm}
        onReject={handleSuggestionReject}
        darkMode
      />
    </>
  );
};
